import { createSlice } from "@reduxjs/toolkit";
import { generateBase64SecretKey } from "../components/ConnectedAuthDataInput";
import { WebhookHealthAction } from "../components/shopifyWebhookHealth";

export const datevSettings = {
    consultantNumber: "",
    clientNumber: "",
    accountNumberLength: "",
    businessYearMonth: "",
    businessYearDay: "",
    skr: null,
    datevDocumentType: "OUTGOING_INVOICE",
    includeDeliveryDate: false,
};

export const initialClient = {
    name: "",
    note: "",
    ...datevSettings,
};

export const initialAccessData = {
    endpoint: "",
    key: "",
    value: "",
    token: "",
    sharedSecret: "",
};

export const initialConnectedAuthData = {
    clientUUID: "",
    type: "",
    title: "",
    ...initialAccessData,
};

// Shopify webhook health (issue #41971). rows is the organisation-wide list the tab renders; byUUID holds the
// state of the per-credential check and repair requests, keyed by authDataUUID, and the modal reads the entry of
// the credential it has open. loaded tells an empty organisation apart from one that has not answered yet.
// Everything in here belongs to one organisation, so it is rebuilt from scratch whenever the clients are.
const emptyShopifyWebhookHealth = () => ({
    rows: [],
    loading: false,
    loaded: false,
    errorMessage: null,
    byUUID: {},
});

const initialState = {
    isLoading: false,
    isEditClientLoading: false,
    isEditAuthDataLoading: false,
    isDeleteClientLoading: false,
    isDeleteAuthDataLoading: false,

    isGlobalDatevSetting: true,

    prepareAuthDataOnboardingLoading: false,

    editClientMode: null,
    editConnectedAuthDataMode: null,

    inputLableInformation: {},

    clients: [],
    editClient: initialClient,
    connectedAuthData: {},
    editConnectedAuthData: {},
    editAccessData: {},
    usedDatevSettings: [],
    usedConnectedAuthData: [],
    authLink: null,

    selectedClient: null,

    editClientModal: false,
    editConnectedAuthDataModalForClient: false,
    editConnectedAuthDataModalWithoutClient: false,
    deleteClientModal: false,
    deleteConnectedAuthDataModal: false,

    errorMessage: null,
    editClientErrorMessage: null,
    editAuthDataErrorMessage: null,
    editAuthDataSuccessMessage: null,
    deleteClientErrorMessage: null,
    deleteAuthDataErrorMessage: null,
    prepareAuthDataOnboardingErrorMessage: null,

    getDatevConnectionsLoading: false,
    getDatevConnectionsErrorMessage: null,
    existingDatevConnections: [],
    datevConnectionsWarningMessage: null,

    addDatevConnectionLoadingId: undefined,
    addDatevConnectionErrorMessage: undefined,

    removeDatevConnectionLoadingId: undefined,
    removeDatevConnectionErrorMessage: undefined,

    searchClientsFromProdLoading: false,
    searchClientsFromProdErrorMessage: null,
    productionClients: [],

    autoUpdateClientFromProdLoading: false,
    autoUpdateClientFromProdErrorMessage: null,
    autoUpdateClientFromProdSuccessMessage: null,

    shopifyWebhookHealth: emptyShopifyWebhookHealth(),
};

// The check and the repair write the same result, so both triplets share these. A repair answers with the
// state after re-checking, which is why it updates the row rather than only clearing its own flag; only the
// wording the modal picks tells the two apart, which is what action carries.
const webhookHealthEntry = (state, authDataUUID) => {
    if (!state.shopifyWebhookHealth.byUUID[authDataUUID]) {
        state.shopifyWebhookHealth.byUUID[authDataUUID] = {
            loading: false,
            dto: null,
            errorMessage: null,
            action: null,
        };
    }
    return state.shopifyWebhookHealth.byUUID[authDataUUID];
};

const startWebhookHealthRequest = (state, authDataUUID, action) => {
    const entry = webhookHealthEntry(state, authDataUUID);
    entry.loading = true;
    entry.errorMessage = null;
    entry.action = action;
};

const finishWebhookHealthRequest = (state, payload) => {
    const health = payload?.response ?? payload;
    const entry = webhookHealthEntry(state, health.authDataUUID);
    entry.loading = false;
    entry.errorMessage = null;
    entry.dto = health;
    state.shopifyWebhookHealth.rows = state.shopifyWebhookHealth.rows.map((row) =>
        row.authDataUUID === health.authDataUUID ? health : row
    );
};

// The previous result is deliberately left in place: a failed repair must not take away the topic table that
// says which webhooks the user was about to fix.
const failWebhookHealthRequest = (state, { authDataUUID, errorMessage }) => {
    const entry = webhookHealthEntry(state, authDataUUID);
    entry.loading = false;
    entry.errorMessage = typeof errorMessage === "string" && errorMessage ? errorMessage : "Unbekannter Fehler";
};

const clientSlice = createSlice({
    name: "Client",
    initialState,
    reducers: {
        getClientsForSelectedOrganizationRequest(state) {
            state.isLoading = true;
            state.errorMessage = null;
            state.editClientErrorMessage = null;
            state.editAuthDataErrorMessage = null;
            state.deleteClientErrorMessage = null;
            state.deleteAuthDataErrorMessage = null;
            state.prepareAuthDataOnboardingErrorMessage = null;
            // Switching organisation goes through this request (refreshBasicData), and the webhook health is
            // per organisation: without the reset the Webhooks tab renders the previous organisation's shops
            // until the new answer lands, and its cached per-credential results for the whole session.
            state.shopifyWebhookHealth = emptyShopifyWebhookHealth();
        },
        getClientsForSelectedOrganizationSuccess(state, action) {
            state.isLoading = false;
            state.clients = action.payload;
        },
        getClientsForSelectedOrganizationFailure(state, action) {
            state.isLoading = false;
            state.errorMessage = action.payload;
        },

        getClientsForParallelTestOrganizationRequest(state) {
            state.isLoading = true;
            state.errorMessage = null;
            state.editClientErrorMessage = null;
            state.editAuthDataErrorMessage = null;
            state.deleteClientErrorMessage = null;
            state.deleteAuthDataErrorMessage = null;
            state.paypalConnectErrorMessage = null;
        },
        getClientsForParallelTestOrganizationSuccess(state, action) {
            state.isLoading = false;
            state.parallelTestOrganizationClients = action.payload;
        },
        getClientsForParallelTestOrganizationFailure(state, action) {
            state.isLoading = false;
            state.errorMessage = action.payload;
        },

        createClientRequest(state) {
            state.isEditClientLoading = true;
        },
        createClientSuccess(state, action) {
            state.isEditClientLoading = false;
            state.editClient = initialClient;
            state.clients = action.payload;
            state.editClientModal = false;
            state.editClientMode = null;
        },
        createClientFailure(state, action) {
            state.isEditClientLoading = false;
            state.editClientErrorMessage = action.payload;
        },
        initializeAdyenAccessData(state, action) {
            const key = generateBase64SecretKey();
            state.editAccessData = { ...initialAccessData, endpoint: action.payload, key: key };
            state.editConnectedAuthData = {
                ...initialConnectedAuthData,
                clientUUID: action.payload,
                type: "ADYEN",
                title: "Adyen",
                endpoint: action.payload,
                key: key,
            };
        },
        updateClientRequest(state) {
            state.isEditClientLoading = true;
        },
        updateClientSuccess(state, action) {
            state.isEditClientLoading = false;
            state.editClient = initialClient;
            state.clients = action.payload;
            state.editClientMode = null;
            state.editClientModal = false;
        },
        updateClientDatevSettingsSuccess(state) {
            state.isEditClientLoading = false;
        },
        updateClientFailure(state, action) {
            state.isEditClientLoading = false;
            state.editClientErrorMessage = action.payload;
        },

        deleteClientRequest(state) {
            state.isDeleteClientLoading = true;
        },
        deleteClientSuccess(state, action) {
            state.isDeleteClientLoading = false;
            state.clients = action.payload;
            state.deleteClientModal = false;
        },
        deleteClientFailure(state, action) {
            state.isDeleteClientLoading = false;
            state.deleteClientErrorMessage = action.payload;
        },

        createConnectedAuthDataForClientRequest(state) {
            state.isEditAuthDataLoading = true;
            state.editAuthDataErrorMessage = null;
            state.editAuthDataSuccessMessage = null;
        },
        createConnectedAuthDataForClientSuccess(state, action) {
            state.isEditAuthDataLoading = false;
            state.editConnectedAuthDataModalForClient = false;
            state.clients = action.payload;
            state.editAuthData = initialConnectedAuthData;
            state.editAuthDataErrorMessage = null;
            state.editAuthDataSuccessMessage = "Verbindung erfolgreich erstellt.";
        },
        createConnectedAuthDataForClientFailure(state, action) {
            state.isEditAuthDataLoading = false;
            state.editAuthDataErrorMessage = action.payload;
        },

        updateConnectedAuthDataForClientRequest(state) {
            state.isEditAuthDataLoading = true;
            state.editAuthDataErrorMessage = null;
        },
        updateConnectedAuthDataForClientSuccess(state, action) {
            state.isEditAuthDataLoading = false;
            state.clients = action.payload;
            state.editAuthData = initialConnectedAuthData;
            state.editConnectedAuthDataModalForClient = false;
            state.editAuthDataErrorMessage = null;
        },
        updateConnectedAuthDataForClientFailure(state, action) {
            state.isEditAuthDataLoading = false;
            state.editAuthDataErrorMessage = action.payload;
        },

        deleteConnectedAuthDataForClientRequest(state) {
            state.isDeleteAuthDataLoading = true;
            state.deleteAuthDataErrorMessage = null;
        },
        deleteConnectedAuthDataForClientSuccess(state, action) {
            state.isDeleteAuthDataLoading = false;
            state.editConnectedAuthDataModalForClient = false;
            state.deleteConnectedAuthDataModal = false;
            state.clients = action.payload;
            state.editAuthData = initialConnectedAuthData;
        },
        deleteConnectedAuthDataForClientFailure(state, action) {
            state.isDeleteAuthDataLoading = false;
            state.deleteAuthDataErrorMessage = action.payload;
        },

        prepareAuthDataOnboardingRequest(state) {
            state.prepareAuthDataOnboardingLoading = true;
            state.prepareAuthDataOnboardingErrorMessage = null;
            state.authLink = null;
        },
        prepareAuthDataOnboardingSuccess(state, action) {
            state.prepareAuthDataOnboardingLoading = false;
            state.authLink = action.payload;
        },
        prepareAuthDataOnboardingFailure(state, action) {
            state.prepareAuthDataOnboardingLoading = false;
            state.prepareAuthDataOnboardingErrorMessage = action.payload;
        },
        setAuthLink(state, action) {
            state.authLink = action.payload;
        },
        setInputLableInformation(state, action) {
            state.inputLableInformation = action.payload;
        },

        setUploadedDataFile(state, action) {
            state.dataFileToImport = action.payload;
        },

        toggleEditClientModal(state) {
            state.editClientModal = !state.editClientModal;
        },
        setEditClient(state, action) {
            state.editClient = action.payload;
        },
        setEditClientMode(state, action) {
            state.editClientMode = action.payload;
        },
        toggleDeleteClientModal(state) {
            state.deleteClientModal = !state.deleteClientModal;
        },
        toggleDeleteConnectedAuthDataModal(state) {
            state.deleteConnectedAuthDataModal = !state.deleteConnectedAuthDataModal;
        },

        toggleEditConnectedAuthDataModalForClient(state) {
            state.editConnectedAuthDataModalForClient = !state.editConnectedAuthDataModalForClient;
            state.connectedAuthData = state.editConnectedAuthData;
        },
        toggleEditConnectedAuthDataModalWithoutClient(state) {
            state.editConnectedAuthDataModalWithoutClient = !state.editConnectedAuthDataModalWithoutClient;
        },
        setEditConnectedAuthData(state, action) {
            state.editConnectedAuthData = action.payload;
        },
        setEditConnectedAuthDataMode(state, action) {
            state.editConnectedAuthDataMode = action.payload;
        },

        setUsedDatevSetting(state, action) {
            state.isGlobalDatevSetting = action.payload.length === 1 || action.payload.length === 0;
            state.usedDatevSettings = action.payload;
        },
        setUsedConnectedAuthData(state, action) {
            state.usedConnectedAuthData = action.payload;
        },

        connectAuthDataWithClientRequest(state) {
            state.subloading = true;
            state.connectAuthDataWithClientError = null;
        },
        connectAuthDataWithClientSuccess(state) {
            state.subloading = false;
        },
        connectAuthDataWithClientFailure(state, action) {
            state.subloading = false;
            state.connectAuthDataWithClientError = action.payload;
        },
        setSelectedClient(state, action) {
            state.selectedClient = action.payload;
        },

        resetErrorMessages(state) {
            state.errorMessage = null;
            state.editClientErrorMessage = null;
            state.editAuthDataErrorMessage = null;
            state.deleteClientErrorMessage = null;
            state.deleteAuthDataErrorMessage = null;
            state.prepareAuthDataOnboardingErrorMessage = null;
        },

        getDatevConnectionsRequest(state) {
            state.getDatevConnectionsLoading = true;
            state.getDatevConnectionsErrorMessage = null;
            state.existingDatevConnections = [];
            state.datevConnectionsWarningMessage = null;
        },
        getDatevConnectionsSuccess(state, action) {
            state.getDatevConnectionsLoading = false;

            if (action.payload.response) {
                state.existingDatevConnections = action.payload.response;
            } else {
                state.existingDatevConnections = action.payload;
            }

            if (action.payload.warningMessage) {
                state.datevConnectionsWarningMessage = action.payload.warningMessage;
            }
        },
        getDatevConnectionsFailure(state, action) {
            state.getDatevConnectionsLoading = false;
            state.getDatevConnectionsErrorMessage = action.payload;
        },

        prepareDatevUOOnboardingRequest(state, action) {
            state.addDatevConnectionLoadingId = action.payload;
            state.authLink = null;
            state.addDatevConnectionErrorMessage = undefined;
        },
        prepareDatevUOOnboardingSuccess(state, action) {
            state.authLink = action.payload;
        },
        prepareDatevUOOnboardingFailure(state, action) {
            state.addDatevConnectionLoadingId = undefined;
            state.addDatevConnectionErrorMessage = action.payload;
        },

        removeDatevConnectionRequest(state, action) {
            state.removeDatevConnectionLoadingId = action.payload;
            state.removeDatevConnectionErrorMessage = undefined;
        },
        removeDatevConnectionSuccess(state, action) {
            state.removeDatevConnectionLoadingId = undefined;
            state.existingDatevConnections = state.existingDatevConnections.filter(
                (c) => c.identifier !== action.payload
            );
        },
        removeDatevConnectionFailure(state, action) {
            state.removeDatevConnectionLoadingId = undefined;
            state.removeDatevConnectionErrorMessage = action.payload;
        },

        setEditAuthDataErrorMessage(state, action) {
            state.editAuthDataErrorMessage = action.payload;
        },

        resetAuthDataMessages(state, action) {
            state.editAuthDataErrorMessage = null;
            state.editAuthDataSuccessMessage = null;
        },

        searchClientsFromProdRequest(state) {
            state.searchClientsFromProdLoading = true;
            state.searchClientsFromProdErrorMessage = null;
            state.productionClients = [];
        },
        searchClientsFromProdSuccess(state, action) {
            state.searchClientsFromProdLoading = false;
            state.productionClients = action.payload;
        },
        searchClientsFromProdFailure(state, action) {
            state.searchClientsFromProdLoading = false;
            state.searchClientsFromProdErrorMessage = action.payload;
        },
        resetSearchClientsFromProd(state) {
            state.searchClientsFromProdLoading = false;
            state.searchClientsFromProdErrorMessage = null;
            state.productionClients = [];
        },

        autoUpdateClientFromProdRequest(state) {
            state.autoUpdateClientFromProdLoading = true;
            state.autoUpdateClientFromProdErrorMessage = null;
            state.autoUpdateClientFromProdSuccessMessage = null;
        },
        autoUpdateClientFromProdSuccess(state, action) {
            state.autoUpdateClientFromProdLoading = false;
            state.clients = action.payload;
            state.autoUpdateClientFromProdSuccessMessage = "Client Update aus Produktion erfolgreich";
        },
        autoUpdateClientFromProdFailure(state, action) {
            state.autoUpdateClientFromProdLoading = false;
            state.autoUpdateClientFromProdErrorMessage = action.payload;
        },
        resetAutoUpdateClientFromProdMessages(state) {
            state.autoUpdateClientFromProdErrorMessage = null;
            state.autoUpdateClientFromProdSuccessMessage = null;
        },

        getShopifyWebhookHealthForOrganizationRequest(state) {
            state.shopifyWebhookHealth.loading = true;
            state.shopifyWebhookHealth.errorMessage = null;
        },
        getShopifyWebhookHealthForOrganizationSuccess(state, action) {
            const rows = action.payload?.response ?? action.payload;
            state.shopifyWebhookHealth.loading = false;
            state.shopifyWebhookHealth.loaded = true;
            state.shopifyWebhookHealth.rows = Array.isArray(rows) ? rows : [];
        },
        getShopifyWebhookHealthForOrganizationFailure(state, action) {
            state.shopifyWebhookHealth.loading = false;
            state.shopifyWebhookHealth.loaded = true;
            state.shopifyWebhookHealth.rows = [];
            state.shopifyWebhookHealth.errorMessage =
                typeof action.payload === "string" && action.payload
                    ? action.payload
                    : "Der Webhook-Status konnte nicht geladen werden.";
        },

        checkShopifyWebhookHealthRequest(state, action) {
            startWebhookHealthRequest(state, action.payload, WebhookHealthAction.CHECK);
        },
        checkShopifyWebhookHealthSuccess(state, action) {
            finishWebhookHealthRequest(state, action.payload);
        },
        checkShopifyWebhookHealthFailure(state, action) {
            failWebhookHealthRequest(state, action.payload);
        },

        repairShopifyWebhooksRequest(state, action) {
            startWebhookHealthRequest(state, action.payload, WebhookHealthAction.REPAIR);
        },
        repairShopifyWebhooksSuccess(state, action) {
            finishWebhookHealthRequest(state, action.payload);
        },
        repairShopifyWebhooksFailure(state, action) {
            failWebhookHealthRequest(state, action.payload);
        },
    },
});

export default clientSlice;
